import { createSlice } from "@reduxjs/toolkit";
import {
  depositToWallet,
  fetchInvoiceDetails,
  fetchInvoices,
  fetchMyPayments,
  fetchSettlements,
  fetchWallet,
  fetchWalletTransactions,
  requestSettlement,
  withdrawFromWallet,
} from "../actions/financialActions";

const initialState = {
  wallet: {
    loading: false,
    error: null,
    data: null,
  },
  transactions: {
    loading: false,
    error: null,
    data: [],
    totalCount: 0,
    pageNumber: 1,
    pageSize: 100,
  },
  settlements: {
    loading: false,
    error: null,
    data: [],
  },
  invoices: {
    loading: false,
    error: null,
    data: [],
  },
  selectedInvoice: {
    loading: false,
    error: null,
    data: null,
  },
  payments: {
    loading: false,
    error: null,
    data: [],
  },
};

const financialSlice = createSlice({
  name: "financial",
  initialState,
  reducers: {
    clearFinancialData: (state) => {
      state.wallet.data = null;
      state.transactions.data = [];
      state.settlements.data = [];
      state.invoices.data = [];
      state.payments.data = [];
    },
  },
  extraReducers: {
    // Wallet
    [fetchWallet.pending]: (state) => {
      state.wallet.loading = true;
      state.wallet.error = null;
    },
    [fetchWallet.fulfilled]: (state, action) => {
      state.wallet.loading = false;
      state.wallet.data = action.payload.data;
    },
    [fetchWallet.rejected]: (state, action) => {
      state.wallet.loading = false;
      state.wallet.error = action.payload;
    },

    // Transactions
    [fetchWalletTransactions.pending]: (state) => {
      state.transactions.loading = true;
      state.transactions.error = null;
    },
    [fetchWalletTransactions.fulfilled]: (state, action) => {
      state.transactions.loading = false;
      state.transactions.data = action.payload.data?.items || [];
      state.transactions.totalCount = action.payload.data?.totalCount || 0;
      state.transactions.pageNumber = action.payload.data?.pageNumber || 1;
      state.transactions.pageSize = action.payload.data?.pageSize || 10;
    },
    [fetchWalletTransactions.rejected]: (state, action) => {
      state.transactions.loading = false;
      state.transactions.error = action.payload;
    },

    // Deposit
    [depositToWallet.fulfilled]: (state, action) => {
      // Add new transaction to the list
      if (action.payload.data) {
        state.transactions.data.unshift(action.payload.data);
      }
      // Refresh wallet balance
      if (state.wallet.data) {
        state.wallet.data.balance =
          action.payload.data?.newBalance || state.wallet.data.balance;
      }
    },

    // Withdraw
    [withdrawFromWallet.fulfilled]: (state, action) => {
      // Add new transaction to the list
      if (action.payload.data) {
        state.transactions.data.unshift(action.payload.data);
      }
      // Refresh wallet balance
      if (state.wallet.data) {
        state.wallet.data.balance =
          action.payload.data?.newBalance || state.wallet.data.balance;
      }
    },

    // Settlements
    [fetchSettlements.pending]: (state) => {
      state.settlements.loading = true;
      state.settlements.error = null;
    },
    [fetchSettlements.fulfilled]: (state, action) => {
      state.settlements.loading = false;
      state.settlements.data = action.payload.data || [];
    },
    [fetchSettlements.rejected]: (state, action) => {
      state.settlements.loading = false;
      state.settlements.error = action.payload;
    },

    // Request Settlement
    [requestSettlement.fulfilled]: (state, action) => {
      if (action.payload.data) {
        state.settlements.data.unshift(action.payload.data);
      }
    },

    // Invoices
    [fetchInvoices.pending]: (state) => {
      state.invoices.loading = true;
      state.invoices.error = null;
    },
    [fetchInvoices.fulfilled]: (state, action) => {
      state.invoices.loading = false;
      state.invoices.data = action.payload.data || [];
    },
    [fetchInvoices.rejected]: (state, action) => {
      state.invoices.loading = false;
      state.invoices.error = action.payload;
    },

    // Invoice Details
    [fetchInvoiceDetails.pending]: (state) => {
      state.selectedInvoice.loading = true;
      state.selectedInvoice.error = null;
    },
    [fetchInvoiceDetails.fulfilled]: (state, action) => {
      state.selectedInvoice.loading = false;
      state.selectedInvoice.data = action.payload.data;
    },
    [fetchInvoiceDetails.rejected]: (state, action) => {
      state.selectedInvoice.loading = false;
      state.selectedInvoice.error = action.payload;
    },

    // Payments
    [fetchMyPayments.pending]: (state) => {
      state.payments.loading = true;
      state.payments.error = null;
    },
    [fetchMyPayments.fulfilled]: (state, action) => {
      state.payments.loading = false;
      state.payments.data = action.payload.data || [];
    },
    [fetchMyPayments.rejected]: (state, action) => {
      state.payments.loading = false;
      state.payments.error = action.payload;
    },
  },
});

export const { clearFinancialData } = financialSlice.actions;

export default financialSlice.reducer;
